import { Link } from 'react-router-dom';
import { FileText, Users, Shield, Clock, CreditCard, Globe } from 'lucide-react';
import { Hero } from '../../components/Hero';
import { AnimatedSection } from '../../components/AnimatedSection';

const patientResources = [
  {
    title: 'New Patient Information',
    description: 'Everything you need to know about your first visit, including forms, what to bring, and what to expect.',
    icon: Users,
    href: '/patient-info/new-patients',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80'
  },
  {
    title: 'Insurance & Billing',
    description: 'Information about accepted insurance plans, coverage verification, and financial policies.',
    icon: CreditCard,
    href: '/patient-info/insurance',
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&q=80'
  },
  {
    title: 'Patient Portal',
    description: 'Access your health information, communicate with our team, and manage your appointments online.',
    icon: Globe,
    href: '/patient-info/portal',
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80'
  }
];

const highlights = [
  {
    icon: Clock,
    title: 'Convenient Scheduling',
    description: 'Flexible appointment times and efficient registration process'
  },
  {
    icon: Shield,
    title: 'Privacy Protection',
    description: 'HIPAA-compliant systems and secure handling of your information'
  },
  {
    icon: FileText,
    title: 'Digital Access',
    description: '24/7 access to your health information through our patient portal'
  }
];

export function Overview() {
  return (
    <div>
      <Hero
        title="Patient Information"
        description="Access important resources, forms, and information to help you prepare for your visit and manage your care."
        image="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80"
      />

      {/* Quick Info Bar */}
      <div className="bg-white shadow-md transform -mt-8 relative z-10">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <AnimatedSection delay={0.1}>
              <div className="flex items-center gap-x-3 bg-gradient-cool p-4 rounded-lg shadow-glow">
                <Clock className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="font-semibold">Mon–Thu: 9:00 AM – 5:00 PM</p>
                  <p className="text-sm text-gray-600">Fri: 9:00 AM – 12:00 PM</p>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="flex items-center gap-x-3 bg-gradient-warm p-4 rounded-lg shadow-glow">
                <Shield className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="font-semibold">HIPAA Compliant</p>
                  <p className="text-sm text-gray-600">Secure information handling</p>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.3}>
              <div className="flex items-center gap-x-3 bg-gradient-healing p-4 rounded-lg shadow-glow">
                <Globe className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="font-semibold">Patient Portal</p>
                  <p className="text-sm text-gray-600">24/7 online access</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* Patient Resources */}
      <div className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Patient Resources</h2>
            <p className="mt-4 text-xl text-gray-600">
              Everything you need to manage your care in one place
            </p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {patientResources.map((resource, index) => {
              const Icon = resource.icon;
              return (
                <AnimatedSection key={resource.title} delay={index * 0.1}>
                  <Link
                    to={resource.href}
                    className="group bg-white rounded-xl shadow-md hover:shadow-glow transition-all overflow-hidden"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={resource.image}
                        alt={resource.title}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-cool mb-4">
                        <Icon className="h-6 w-6 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors mb-2">
                        {resource.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {resource.description}
                      </p>
                      <div className="flex items-center text-blue-600 font-medium">
                        Learn more
                        <FileText className="ml-2 h-4 w-4" />
                      </div>
                    </div>
                  </Link>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </div>

      {/* Highlights Section */}
      <div className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Why Choose Us?</h2>
            <p className="mt-4 text-xl text-gray-600">
              Making your healthcare experience seamless and secure
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {highlights.map((highlight, index) => {
              const Icon = highlight.icon;
              return (
                <AnimatedSection key={index} delay={index * 0.1}>
                  <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-glow transition-all">
                    <div className="h-12 w-12 bg-gradient-cool rounded-lg flex items-center justify-center mb-6">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{highlight.title}</h3>
                    <p className="text-gray-600">{highlight.description}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-emory-blue to-emory-light-blue">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center">
            <AnimatedSection>
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Ready to Get Started?
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-blue-100">
                Schedule your appointment today and take the first step towards better neurological health.
              </p>
              <div className="mt-8 flex justify-center gap-x-4">
                <Link
                  to="/contact"
                  className="rounded-md bg-white px-6 py-3 text-base font-semibold text-blue-700 shadow-sm hover:bg-blue-50 transition-all transform hover:-translate-y-0.5"
                >
                  Schedule Now
                </Link>
                <Link
                  to="/patient-info/new-patients"
                  className="rounded-md border border-white bg-transparent px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-blue-800/20 transition-all"
                >
                  New Patient Info
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </div>
  );
}